// The 2-minute match quiz.
//
// Six questions, kept separate from the component so the wording — which is the
// part that actually matters here — can be read and changed without picking
// through JSX.
//
// Every answer maps onto something the rest of the site already understands:
// `condition` values are the category names the API returns, and `availability`,
// `language` and `forWho` are the exact strings the search bar and
// backend/enquiries.py accept. That is what lets the result hand straight over
// to the enquiry form instead of restating the query in a second vocabulary.

/**
 * Selecting this in Q2 puts crisis support in front of the person before the
 * quiz continues. It is not scored, and it never becomes a "condition" — the
 * answer to "I have thoughts of harming myself" is a phone number, not a
 * directory listing.
 */
export const CRISIS_VALUE = 'crisis';

export const QUESTIONS = [
  {
    id: 'forWho',
    prompt: 'Who would the sessions be for?',
    help: 'This changes who we look for more than anything else you tell us.',
    type: 'single',
    options: [
      { value: 'Myself', label: 'Me' },
      { value: 'Me and my partner', label: 'Me and my partner' },
      { value: 'My child', label: 'My child' },
      { value: 'A family member', label: 'Someone in my family' },
      { value: 'My team', label: 'My team at work' },
    ],
  },
  {
    id: 'struggles',
    prompt: 'What has been hardest lately?',
    help: 'Pick up to three. There are no wrong answers, and you can change your mind later.',
    type: 'multi',
    limit: 3,
    options: [
      { value: 'Anxiety Treatment', label: 'Worry I can’t switch off' },
      { value: 'Depression', label: 'Feeling flat, low or numb' },
      { value: 'Trauma & PTSD', label: 'Something that happened to me' },
      { value: 'Relationships', label: 'Things at home, or with a partner' },
      { value: 'ADHD', label: 'Focus, time and getting started' },
      { value: 'Grief & Loss', label: 'Losing someone' },
      { value: 'Burnout & Stress', label: 'Work, pressure and exhaustion' },
      { value: 'Sleep Problems', label: 'Sleep' },
      { value: 'Self-Esteem', label: 'How I feel about myself' },
      { value: 'OCD', label: 'Thoughts or rituals I can’t stop' },
      { value: 'Eating Disorders', label: 'Food, eating or my body' },
      { value: 'Addiction Recovery', label: 'Drinking, substances or a habit' },
      { value: CRISIS_VALUE, label: 'Thoughts of harming myself' },
    ],
  },
  {
    id: 'duration',
    prompt: 'Roughly how long has that been going on?',
    type: 'single',
    options: [
      { value: 'A few weeks', label: 'A few weeks' },
      { value: 'A few months', label: 'A few months' },
      { value: 'About a year', label: 'About a year' },
      { value: 'Years — on and off', label: 'Years, on and off' },
    ],
  },
  {
    id: 'goal',
    prompt: 'What would help most right now?',
    type: 'single',
    options: [
      { value: 'Someone to talk it through with', label: 'Someone to talk it through with' },
      { value: 'Practical tools to cope day to day', label: 'Practical tools for day to day' },
      { value: 'To understand why this keeps happening', label: 'To understand why this keeps happening' },
      { value: 'An assessment or a diagnosis', label: 'An assessment or a diagnosis' },
      { value: 'Not sure yet', label: 'I’m not sure yet' },
    ],
  },
  {
    id: 'availability',
    prompt: 'When could you usually make a session?',
    // Values are AVAILABILITY in SearchBar.jsx, verbatim.
    type: 'single',
    options: [
      { value: 'Evenings', label: 'Evenings, after work' },
      { value: 'Weekends', label: 'Weekends' },
      { value: 'This week', label: 'As soon as possible' },
      { value: 'Any time', label: 'I’m flexible' },
    ],
  },
  {
    id: 'language',
    prompt: 'Which language would you rather talk in?',
    help: 'Therapy happens in words. Say the one you think in, not the one you work in.',
    // Values are LANGUAGES in SearchBar.jsx, verbatim.
    type: 'single',
    options: [
      'English',
      'Hindi',
      'Bengali',
      'Marathi',
      'Telugu',
      'Tamil',
      'Gujarati',
      'Urdu',
      'Kannada',
      'Malayalam',
      'Odia',
      'Punjabi',
      'Assamese',
      'Any language',
    ].map((value) => ({ value, label: value })),
  },
];

/**
 * Turns answers into a search query plus a note for whoever picks this up.
 *
 * The first struggle chosen is the condition. Not the "strongest" — nothing
 * here can rank someone's suffering, and pretending to would be the kind of
 * false precision this quiz has no business inventing. What the rest of the
 * answers earn is context in the note, which a human reads.
 */
export function buildResult(answers) {
  const struggles = (answers.struggles || []).filter((v) => v !== CRISIS_VALUE);

  const query = {
    condition: struggles[0] || '',
    availability: answers.availability || 'Any time',
    language: answers.language || 'Any language',
    forWho: answers.forWho || 'Myself',
  };

  const lines = [];
  if (struggles.length) lines.push(`Hardest right now: ${struggles.join(', ')}.`);
  if (answers.duration) lines.push(`Going on for: ${answers.duration.toLowerCase()}.`);
  if (answers.goal) lines.push(`Hoping for: ${answers.goal.toLowerCase()}.`);

  return {
    ...query,
    also: struggles.slice(1),
    notes: lines.length ? `From the 2-minute quiz — ${lines.join(' ')}` : '',
  };
}

export function isComplete(answers, question) {
  const value = answers[question.id];
  return question.type === 'multi' ? Boolean(value?.length) : Boolean(value);
}
